import React from "react";
import type { LabArrSliceResult } from "../../models/labArr";

function companyLabel(company: "openai" | "anthropic") {
  return company === "openai" ? "OpenAI" : "Anthropic（Claude）";
}

function biasLabel(meanErrorB: number) {
  if (Math.abs(meanErrorB) < 0.05) return "无明显偏差";
  return meanErrorB < 0
    ? `平均低估 $${Math.abs(meanErrorB).toFixed(1)}B`
    : `平均高估 $${meanErrorB.toFixed(1)}B`;
}

export function LabArrPanel({ result, mode }: { result: LabArrSliceResult; mode: "monitor" | "forecast" }) {
  if (mode === "monitor") {
    const combinedBenchmark = result.monitor.externalBenchmarks.find((benchmark) => benchmark.scope === "combined");
    return (
      <div className="lab-arr-panel">
        <p className="lab-arr-panel__note"><b>ARR / run-rate = 当前收入速度 × 12。</b>它不是过去十二个月 GAAP 收入，也不是未来十二个月累计收入；公开锚点、纯模型 nowcast、外部校准与未来预测严格分开。</p>
        <div className="lab-arr-panel__companies">
          {Object.values(result.companies).map((company) => {
            const benchmark = result.monitor.externalBenchmarks.find((item) => item.scope === company.company && !item.calibrationEligible);
            const metrics = company.backtest.primary.metrics.full;
            return (
              <article key={company.company}>
                <header><h4>{companyLabel(company.company)}</h4><span>{company.forecast.selectedModel}</span></header>
                <strong>${company.monitor.currentDateNowcastArrB.toFixed(1)}B</strong>
                <small>{company.monitor.currentDate} · 当前 ARR（年化收入运行率）</small>
                <dl>
                  {company.monitor.currentCalibration ? <div><dt>当前层级</dt><dd>外部研究校准 · 非公开披露</dd></div> : null}
                  <div><dt>纯模型 nowcast</dt><dd>${company.monitor.statisticalCurrentDateNowcastArrB.toFixed(1)}B · 未校准</dd></div>
                  <div><dt>最新完整月</dt><dd>${company.monitor.latestCompleteMonthNowcastArrB.toFixed(1)}B · {company.monitor.latestCompleteMonth}</dd></div>
                  <div><dt>公开锚点</dt><dd>${company.monitor.latestAnchor.arrB.toFixed(1)}B · {company.monitor.latestAnchor.observedAt}</dd></div>
                  {benchmark ? <div><dt>外部参照</dt><dd>{benchmark.exactness === "lower-bound" ? ">" : "≈"}${benchmark.valueB.toFixed(0)}B · {benchmark.asOf}</dd></div> : null}
                  <div><dt>历史误差</dt><dd>OOS {metrics.observations} · MAPE {metrics.mape.toFixed(1)}% · {biasLabel(metrics.meanErrorB)}</dd></div>
                </dl>
              </article>
            );
          })}
        </div>
        {combinedBenchmark ? (
          <p className="lab-arr-panel__benchmark">
            当前校准拆分为 OpenAI ${result.companies.openai.monitor.currentDateNowcastArrB.toFixed(0)}B、Anthropic ${result.companies.anthropic.monitor.currentDateNowcastArrB.toFixed(0)}B。SemiAnalysis {combinedBenchmark.asOf} 公开摘要只披露两家公司合计约 ${combinedBenchmark.valueB.toFixed(0)}B；精确拆分未公开，且 Anthropic gross 与 OpenAI net-ish 口径可能不可直接相加。
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="model-panel-table lab-arr-panel">
      <p className="lab-arr-panel__note"><b>下表是预测终点时的 ARR 年化速度，不是未来 12 个月累计收入。</b>终值超出真实锚点预测跨度，属于显式外推。偏差定义为“预测－实际”：负数表示模型历史上偏低。</p>
      <div className="lab-arr-panel__table-wrap">
        <table>
          <thead><tr><th>公司</th><th>{result.companies.openai.forecast.points.at(-1)!.month} 月末 ARR</th><th>已验证跨度</th><th>OOS</th><th>MAPE</th><th>全窗偏差</th><th>最近 2 次偏差</th></tr></thead>
          <tbody>{Object.values(result.companies).map((company) => (
            <tr key={company.company}>
              <td>{companyLabel(company.company)}</td>
              <td>${company.forecast.points.at(-1)!.totalArrB.toFixed(1)}B</td>
              <td>{company.forecast.validatedHorizonMonths} 个月</td>
              <td>{company.backtest.primary.metrics.full.observations}</td>
              <td>{company.backtest.primary.metrics.full.mape.toFixed(1)}%</td>
              <td>{biasLabel(company.backtest.biasDiagnostics.fullWindow.meanErrorB)}</td>
              <td>{biasLabel(company.backtest.biasDiagnostics.recentWindow.meanErrorB)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <details className="lab-arr-panel__diagnostics">
        <summary>建模诊断：冠军模型、RMSE 与方向性偏差</summary>
        {Object.values(result.companies).map((company) => (
          <p key={company.company}>
            <b>{companyLabel(company.company)}</b>：{company.backtest.primary.label}；RMSE ${company.backtest.primary.metrics.full.rmseB.toFixed(2)}B；
            历史低估率 {company.backtest.biasDiagnostics.fullWindow.underforecastRatePct.toFixed(0)}%；{company.backtest.promotionGate.reason}
          </p>
        ))}
      </details>
    </div>
  );
}
